import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { dsClass } from '../../common/constants/dsClasses.js';
import { approverContactMsg, cancelMsg, redeemIsBinding, t } from '../../common/i18n/index.js';
import { ContactDropdown } from '../../common/react-hook-form/fields/ContactDropdown.js';
import { findPersonBillingAccount } from '../../common/utils/stateUtils.js';
import { EppRedeemTerminateRequestType } from '../../generated/api/models.js';
import { Loading } from '../Loading/index.js';
import {
  PersonalBillingDetails,
  createPersonBillingDetailsData,
} from '../PersonalBillingDetails/PersonalBillingDetails.js';
import { WizardActions } from '../WizardActions/index.js';
import { RedeemOptionDetails } from './EppRedeemOptionDetails.js';
import { useEmployeeEppRedeemDialog } from './EppRedeemUtils.js';
import type { EmployeeSubscription } from '../../common/types/subscription.js';
import type { BillingAccount, Contact } from '../../generated/api/models.js';
import type { AuthenticatedUser } from '../../public/site/authState';

import './EppRedeem.scss';

export interface EppRedeemEmployeeProps {
  billingAccounts?: BillingAccount[] | null;
  contacts?: Contact[];
  email?: string;
  firstName?: string;
  lastName?: string;
  loggedInUserContactId?: string;
  mobile?: string;
  onBackClick: () => void;
  subscription: EmployeeSubscription;
}

const getApproverOptions = (contacts: Contact[] = [], contactIdToFilterOut?: string): Contact[] =>
  contactIdToFilterOut ? contacts.filter(item => item.contactId !== contactIdToFilterOut) : contacts;

export const EppRedeemEmployee = ({
  billingAccounts,
  contacts,
  email,
  firstName,
  lastName,
  loggedInUserContactId,
  mobile,
  onBackClick,
  subscription,
}: EppRedeemEmployeeProps) => {
  const [approverContact, setApproverContact] = useState('');
  const personBillingAccount = findPersonBillingAccount(billingAccounts || undefined);
  const personBillingDetailsData = createPersonBillingDetailsData(
    {
      firstName,
      lastName,
      email,
      mobile,
    } as AuthenticatedUser,
    personBillingAccount
  );
  const methods = useForm({ values: personBillingDetailsData });
  const personalBillingDetails = methods.watch();

  const forwardClick = useEmployeeEppRedeemDialog(
    subscription,
    personBillingAccount?.billingAccountId,
    approverContact,
    personalBillingDetails,
    firstName,
    lastName
  );

  return billingAccounts && contacts ? (
    <div className="of-epp-redeem">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(forwardClick)}>
          <div className={`${dsClass.PADDING_TOP_2} ${dsClass.PADDING_BOTTOM_5}`}>
            {`${t.CW9X(
              'Redemption requires the approval of your employer. Redemption request is automatically sent to your employer. After approval, the invoice will be sent to you via email.'
            )} ${t.UNYV(redeemIsBinding)}`}
          </div>
          <RedeemOptionDetails
            subscription={subscription}
            selectedOption={EppRedeemTerminateRequestType.EMPLOYEE_REDEEM}
          />
          <>
            <ContactDropdown
              canAddNewContacts={false}
              className={dsClass.MARGIN_0}
              name="approverContact"
              labelText={t.GOZ7(approverContactMsg)}
              contacts={getApproverOptions(contacts, loggedInUserContactId)}
              onChangeContact={setApproverContact}
            />
            <PersonalBillingDetails editing={personBillingAccount === undefined} data={personBillingDetailsData} />
          </>
          <hr className={dsClass.MARGIN_VERTICAL_4} />
          <WizardActions
            forwardButtonText={t.I62A('Continue')}
            backButtonText={t.B2V1(cancelMsg)}
            onForwardClick={() => undefined}
            onBackClick={onBackClick}
          />
        </form>
      </FormProvider>
    </div>
  ) : (
    <Loading />
  );
};
